import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Storing, WeekZonderOverzicht } from '../components/MeasurementState';
import { PeriodPicker } from '../components/PeriodPicker';
import { SignalList } from '../components/SignalList';
import { useMeldToestand } from '../components/Toestandsmelding';
import { FOCUSRING } from '../focus';
import { useGekozenWeek } from '../hooks/useGekozenWeek';
import { useSignaleringen } from '../hooks/useOverzicht';
import { STORING_ONBEREIKBAAR, vorigeWeekVan, weektoestand } from '../toestand';

/**
 * Nakijkers (S5, `/werkkwaliteit/nakijkers`, CAP-7): de velden waar nakijkers sterk verschillen. Eén
 * lijst per week over beide werksoorten samen (besluit 31-10-2026 #1), dus een Weekkiezer en geen
 * Werksoortschakelaar; de werksoort in het adres blijft staan en reist mee terug naar Team.
 *
 * De Nakijkers-uitleg staat er in elke toestand, ook tijdens het laden en bij een storing: hij zegt
 * waarom deze pagina bestaat, en dat hangt niet af van wat de meting deze week opleverde.
 */
export function NakijkersPage() {
  const { t } = useTranslation();
  const { week, weken, kies } = useGekozenWeek();
  const signalen = useSignaleringen(week);
  const vorigeWeek = vorigeWeekVan(weken, week);
  const inhoudRef = useRef<HTMLDivElement>(null);
  // Na "Opnieuw proberen" dat lukt, gaat de focus naar de eerste kop van de nieuwe inhoud; lukt het
  // niet, dan blijft hij op de melding (EXPERIENCE.md, Toestandsmelding).
  const herpoging = useRef(false);

  const antwoord = signalen.data;
  const storing =
    signalen.isError ||
    antwoord === undefined ||
    (antwoord.data === null && weektoestand(antwoord.reden) !== 'nog_niet_gemeten');

  useEffect(() => {
    herpoging.current = false;
  }, [week]);

  useEffect(() => {
    if (!herpoging.current || signalen.isLoading || storing) return;
    herpoging.current = false;
    const doel = inhoudRef.current?.querySelector<HTMLElement>('h2, h3') ?? inhoudRef.current;
    doel?.focus();
  });

  function opnieuw() {
    herpoging.current = true;
    void signalen.refetch();
  }

  function inhoud() {
    if (weken.length === 0) return null;
    if (signalen.isLoading) return <NakijkersLaden />;
    if (signalen.isError || antwoord === undefined) {
      return <Storing onHerlaad={opnieuw} oorzaak={STORING_ONBEREIKBAAR} vorigeWeek={vorigeWeek} week={week} />;
    }
    // Nog niet gemeten is iets anders dan een mislukte meting (AD-25), en geen van beide leest als
    // "niemand liep uiteen".
    if (antwoord.data === null) {
      return <WeekZonderOverzicht antwoord={antwoord} onHerlaad={opnieuw} vorigeWeek={vorigeWeek} week={week} weken={weken} />;
    }
    return <SignalList antwoord={antwoord.data} />;
  }

  return (
    <div className="flex min-w-0 flex-col gap-5" data-testid="werkkwaliteit-nakijkers">
      <PeriodPicker onKies={kies} waarde={week} weken={weken} />
      <section
        aria-labelledby="werkkwaliteit-nakijkers-uitleg-kop"
        className="flex flex-col gap-1.5 rounded-xl border border-[var(--border-soft)] bg-[var(--background)] p-4 text-[0.8125rem] leading-relaxed text-[var(--foreground)]"
        data-testid="werkkwaliteit-nakijkers-uitleg"
      >
        <h2 className="text-[0.9375rem] font-semibold text-[var(--foreground-heading)]" id="werkkwaliteit-nakijkers-uitleg-kop">
          {t('qa.nakijkers.uitlegKop')}
        </h2>
        {/* Uitzondering D-4 (Friso 2026-11-04): de feedbackrapporten zijn alleen Nederlands, ook bij een Engelse weergave. */}
        <p>{t('qa.nakijkers.uitlegVerschil', { lng: 'nl' })}</p>
        <p>{t('qa.nakijkers.uitlegCorrectie', { lng: 'nl' })}</p>
        <p className="font-medium">{t('qa.nakijkers.uitlegBespreking', { lng: 'nl' })}</p>
      </section>
      <div className={`flex min-w-0 flex-col rounded-xl ${FOCUSRING}`} ref={inhoudRef} tabIndex={-1}>
        {inhoud()}
      </div>
    </div>
  );
}

/** Laden: een skelet in de vorm van twee kaarten, nooit nullen als tussenstand. */
function NakijkersLaden() {
  const { t } = useTranslation();
  useMeldToestand(t('qa.eigen.laden'));

  return (
    <div aria-busy="true" className="grid gap-3 xl:grid-cols-2" data-testid="werkkwaliteit-nakijkers-laden">
      <span className="sr-only">{t('qa.eigen.laden')}</span>
      {[0, 1].map((kaart) => (
        <div
          aria-hidden="true"
          className="flex flex-col gap-3 rounded-xl border border-[var(--border-soft)] p-4"
          data-werkkwaliteit-skelet="kaart"
          key={kaart}
        >
          <div className="h-4 w-2/5 rounded bg-[var(--background-muted)]" />
          <div className="h-3 w-3/5 rounded bg-[var(--background-muted)]" />
          <div className="h-2 rounded-full bg-[var(--background-muted)]" />
          <div className="h-2 rounded-full bg-[var(--background-muted)]" />
        </div>
      ))}
    </div>
  );
}
